import axiosInstance from "../../api/axiosInstance";

export class BillingError extends Error {
    constructor(message, status) {
        super(message);
        this.name = "BillingError";
        this.status = status;
    }
}

const messageFrom = (err, fallback) => {
    const data = err?.response?.data;
    if (data?.errors) {
        const msgs = Object.values(data.errors).flat().filter(Boolean);
        if (msgs.length) return msgs.join(", ");
    }
    return data?.message || data?.error || err?.message || fallback;
};

const request = async (fn, fallback) => {
    try {
        const res = await fn();
        const body = res?.data;
        if (body?.success === false) {
            throw new BillingError(body.message || fallback, res?.status);
        }
        return body;
    } catch (err) {
        if (err instanceof BillingError) throw err;
        throw new BillingError(messageFrom(err, fallback), err?.response?.status);
    }
};

export const extractPage = (body) => {
    const payload = body?.data;
    if (payload && Array.isArray(payload.data)) {
        const pageSize = payload.per_page || payload.data.length || 1;
        const total = payload.total ?? payload.data.length;
        return {
            rows: payload.data,
            page: payload.current_page || 1,
            lastPage: payload.last_page || Math.max(1, Math.ceil(total / pageSize)),
            total,
        };
    }
    if (Array.isArray(payload)) {
        const meta = body?.meta || {};
        return {
            rows: payload,
            page: meta.current_page || 1,
            lastPage: meta.last_page || 1,
            total: meta.total ?? payload.length,
        };
    }
    return { rows: [], page: 1, lastPage: 1, total: 0 };
};

export const getWallet = () =>
    request(() => axiosInstance.get("/billing/wallet"), "Could not load wallet").then((body) => {
        const data = body?.data ?? {};
        return {
            user_id: data.user_id,
            wallet_user_id: data.wallet_user_id,
            billing_type: data.billing_type ?? null,
            current_credit: Number(data.current_credit ?? 0),
        };
    });

export const listTransactions = (params) =>
    request(
        () => axiosInstance.get("/billing/transactions", { params }),
        "Could not load the ledger"
    ).then(extractPage);

export const listTopups = (params) =>
    request(
        () => axiosInstance.get("/billing/topups", { params }),
        "Could not load top-ups"
    ).then(extractPage);

export const createCreditPayment = (amountRupees) =>
    request(
        () => axiosInstance.post("/payment", { amount: amountRupees, purpose: "credit_topup" }),
        "Could not start the payment"
    ).then((body) => body?.data ?? body);

export const confirmCreditPayment = ({ paymentId, orderId, signature }) =>
    request(
        () => axiosInstance.post("/payment/success", {
            payment_id: paymentId,
            order_id: orderId,
            signature,
        }),
        "Could not confirm the payment"
    );
